import { useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* ------------------------------------------------------------------ */
/*  Board search — one box over a screen's columns                      */
/* ------------------------------------------------------------------ */
/* Asks the API rather than filtering what is on screen: the columns only
   ever hold one epic's worth at a time. Each screen searches what its own
   columns show — a `scope` names the endpoint, the groups (in order) and the
   wording. Each group may give:
     context(item)  — the grey "where it lives" note on the right
     pickable(item) — false shows the row but it cannot be chosen
     idOf(item)     — a stable id when `key` can repeat or be empty        */

/* Requirement Board: Jira epics, stories and Xray tests. Each Test carries
   the story it belongs to, so picking it selects epic → story → test. */
export const BOARD_SCOPE = {
  path: "coverage/board-search",
  placeholder: "Search epics, stories and tests",
  describes: "epics, stories and tests",
  groups: [
    { id: "epics", title: "Epics", icon: Icons.JiraEpic, color: "text-purple-600" },
    {
      id: "stories", title: "Stories", icon: Icons.JiraStory, color: "text-emerald-600",
      context: (it) => (it.epic_key ? `in ${it.epic_key}` : ""),
    },
    {
      id: "tests", title: "Tests", icon: Icons.JiraXrayTest, color: "text-teal-600",
      context: (it) =>
        it.story_key ? `in ${it.story_key}` : it.epic_key ? `on epic ${it.epic_key}` : "not linked to a story",
      // Linked to no story and no epic: nothing on the board to take you to.
      pickable: (it) => Boolean(it.story_key || it.epic_key),
    },
  ],
};

/* Artifacts screen: epics, saved Artifacts (Playwright scripts) and their
   executions — the screen's three columns. */
export const ARTIFACT_SCOPE = {
  path: "epics/search",
  placeholder: "Search epics, artifacts and trial runs",
  describes: "epics, artifacts and trial runs",
  groups: [
    {
      id: "epics", title: "Epics", icon: Icons.JiraEpic, color: "text-purple-600",
      context: (it) =>
        it.script_count ? `${it.script_count} artifact${it.script_count === 1 ? "" : "s"}` : "no artifacts",
    },
    {
      id: "artifacts", title: "Artifacts", icon: Icons.Script, color: "text-blue-600",
      idOf: (it) => it.testcasekey,
      context: (it) => (it.story_key ? `in ${it.story_key}` : it.epic_key ? `in ${it.epic_key}` : ""),
    },
    {
      /* As the Artifacts screen's column shows them: Trial runs, the cards' green ▶. */
      id: "executions", title: "Trial runs", icon: Icons.Play, color: "text-emerald-600",
      idOf: (it) => it.executionkey,
      context: (it) =>
        [it.run_status ? it.run_status.replace(/_/g, " ") : "", it.xray_test_key ? `of ${it.xray_test_key}` : ""]
          .filter(Boolean)
          .join(" · "),
    },
  ],
};

export default function BoardSearch({ project, disabled = false, onPick, scope = BOARD_SCOPE }) {
  const SEARCH_GROUPS = scope.groups;
  const emptyResults = () => Object.fromEntries(SEARCH_GROUPS.map((g) => [g.id, []]));
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const seq = useRef(0);

  // A new project is a new search space.
  useEffect(() => {
    setQuery("");
    setResults(null);
  }, [project]);

  /* Debounced; a bare number ("1249") is a key search, so one digit is
     enough — otherwise wait for two characters. Stale replies are dropped. */
  useEffect(() => {
    const q = query.trim();
    if (!project || !(q.length >= 2 || /^\d+$/.test(q))) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    const mine = ++seq.current;
    /* Drop the previous query's results so the loading panel shows for this
       one too — otherwise editing a finished search (e.g. backspacing "198"
       to "19") kept the stale list up with only the tiny input spinner. */
    setResults(null);
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}${scope.path}?project=${encodeURIComponent(project)}` +
            `&q=${encodeURIComponent(q)}`
        );
        const body = await res.json().catch(() => ({}));
        if (mine !== seq.current) return;
        setResults(
          res.ok
            ? body?.data || null
            : { ...emptyResults(), errors: { all: body?.detail || `HTTP ${res.status}` } }
        );
        setActive(0);
      } catch {
        if (mine === seq.current) setResults({ ...emptyResults(), errors: { all: "Network error" } });
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, project, scope.path]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  /* One flat list in display order, for the arrow keys. */
  const flat = useMemo(() => {
    if (!results) return [];
    const rows = [];
    for (const g of SEARCH_GROUPS) {
      for (const item of results[g.id] || []) {
        rows.push({ group: g.id, item, pickable: g.pickable ? g.pickable(item) : true });
      }
    }
    return rows;
  }, [results, SEARCH_GROUPS]);

  const pick = (row) => {
    if (!row?.pickable) return;
    onPick?.(row.group, row.item);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!flat.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + flat.length) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(flat[active]);
    }
  };

  const showPanel = open && query.trim() && (loading || results);
  const errors = Object.values(results?.errors || {});
  let rowIndex = -1;

  return (
    <div ref={wrapRef} className="relative min-w-[16rem] flex-1">
      <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        role="combobox"
        aria-expanded={Boolean(showPanel)}
        aria-controls="board-search-results"
        aria-autocomplete="list"
        aria-activedescendant={showPanel && flat[active] ? `board-search-${active}` : undefined}
        placeholder={scope.placeholder}
        title={`Searches ${scope.describes} across the whole project. A key or bare number finds that issue, e.g. "1249" finds PSR-1249.`}
        className="h-[2.375rem] w-full rounded-lg border border-slate-200 bg-card py-2 pl-9 pr-8 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-slate-50"
      />
      {loading && (
        <Icons.Spinner className="absolute right-8 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-300" />
      )}
      {query && (
        <button
          type="button"
          onClick={() => {
            setQuery("");
            setOpen(false);
          }}
          title="Clear search"
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <Icons.X className="h-4 w-4" />
        </button>
      )}

      {showPanel && (
        <div
          id="board-search-results"
          role="listbox"
          aria-label="Search results"
          className="cq-scroll absolute left-0 right-0 top-full z-40 mt-1 max-h-[26rem] overflow-y-auto rounded-xl border border-slate-200 bg-card py-1 shadow-lg"
        >
          {loading && !results ? (
            /* Spinner + message, then placeholder rows shaped like the
               Epics / Stories / Tests groups the results will fill. */
            <div role="status" aria-live="polite" className="px-3 py-3">
              <div className="flex items-center gap-2.5 rounded-lg bg-blue-50/60 px-3 py-2.5">
                <Icons.Spinner className="h-5 w-5 flex-none animate-spin text-blue-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">
                    Searching for "{query.trim()}"…
                  </p>
                  <p className="text-[0.6875rem] text-slate-500">
                    Looking through {scope.describes} in {project}
                  </p>
                </div>
              </div>
              <div className="mt-3 space-y-3 animate-pulse" aria-hidden="true">
                {SEARCH_GROUPS.map((g) => (
                  <div key={g.id}>
                    <p className="mb-1.5 text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">
                      {g.title}
                    </p>
                    {[0, 1].map((i) => (
                      <div key={i} className="flex items-center gap-2 py-1">
                        <span className="h-4 w-4 flex-none rounded bg-slate-200" />
                        <span className="h-3 w-16 flex-none rounded bg-slate-200" />
                        <span className={`h-3 rounded bg-slate-100 ${i ? "w-1/3" : "w-1/2"}`} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : flat.length === 0 ? (
            <p className="px-3 py-3 text-xs text-slate-400">
              {errors.length ? `Search failed: ${errors.join(" · ")}` : `Nothing matches "${query.trim()}".`}
            </p>
          ) : (
            SEARCH_GROUPS.map((g) => {
              const items = results?.[g.id] || [];
              if (!items.length) return null;
              const GIcon = g.icon;
              return (
                <div key={g.id} role="group" aria-label={g.title}>
                  <p className="px-3 pb-1 pt-2 text-[0.625rem] font-bold uppercase tracking-wider text-slate-500">
                    {g.title}
                  </p>
                  {items.map((item) => {
                    rowIndex += 1;
                    const idx = rowIndex;
                    const row = flat[idx];
                    const context = g.context ? g.context(item) : "";
                    return (
                      <button
                        key={`${g.id}-${g.idOf ? g.idOf(item) : item.key}`}
                        id={`board-search-${idx}`}
                        type="button"
                        role="option"
                        aria-selected={idx === active}
                        aria-disabled={!row?.pickable}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => pick(row)}
                        className={[
                          "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm",
                          !row?.pickable
                            ? "cursor-default opacity-50"
                            : idx === active
                              ? "bg-blue-50"
                              : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        {/* 20px like every issue-type icon; `color` only
                            matters for non-Jira icons (Artifacts' Script). */}
                        <GIcon className={`h-5 w-5 flex-none ${g.color || ""}`} />
                        {item.key && (
                          <span className="flex-none font-medium text-blue-600">{item.key}</span>
                        )}
                        <span className="min-w-0 flex-1 truncate text-slate-700">
                          {item.summary || "(no summary)"}
                        </span>
                        {context && (
                          <span className="flex-none text-[0.6875rem] text-slate-400">{context}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
          {flat.length > 0 && errors.length > 0 && (
            <p className="border-t border-slate-100 px-3 py-1.5 text-[0.6875rem] text-amber-700">
              Some results could not be loaded: {errors.join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
